import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { request, gql, Variables } from 'graphql-request';

import { QueryKeys } from 'src/constants/queryKeys';

import { Position } from '@app/types/pool';
import { useConfig } from '@app/config';
import { getTokensWhitelist } from '@app/constants/tokensWhitelist';

const fetcher = (
  baseUrl: string,
  params: Variables = {
    skip: 0,
    first: 10,
    orderBy: 'created',
    orderDirection: 'desc',
    search: '',
    address: ''
  }
) => {
  return request<{ liquidityPositions: Position[] }>(
    `${baseUrl}`,
    gql`
      query {
        liquidityPositions (
          skip: ${params.skip},
          first: ${params.first},
          where: {
            user: "${params.address}"
          }
        ) {
          id
          pair {
            id
            reserve0
            reserve1
            reserveETH
            reserveUSD
            token0Price
            token1Price
            volumeUSD
            totalSupply
            token0 {
              id
              totalSupply
              symbol
              name
              decimals
              tradeVolumeUSD
              tradeVolume
              totalLiquidity
              txCount
              derivedETH
            }
            token1 {
              id
              totalSupply
              symbol
              name
              decimals
              tradeVolumeUSD
              tradeVolume
              totalLiquidity
              txCount
              derivedETH
            }
          }
          liquidityTokenBalance
        }
      }
    `,
    params
  );
};

export const useVerifiedPositionsListData = (
  params: Variables = {},
  options: UseQueryOptions<Position[]> = {}
) => {
  const config = useConfig();

  return useQuery<Position[]>(
    [QueryKeys.VERIFIED_USER_POSITIONS, { params }],
    async () => {
      const response = await fetcher(config?.URLS.subgraphUrl as string, {
        ...params
      });

      const TOKENS_WHITELIST = getTokensWhitelist(config);

      const wlTokensIds = TOKENS_WHITELIST.map(t =>
        t.contractAddress.toLowerCase()
      );

      const pools = response.liquidityPositions
        .filter(item => {
          const { pair } = item;

          if (
            wlTokensIds.includes(pair.token0.id.toLowerCase()) &&
            wlTokensIds.includes(pair.token1.id.toLowerCase())
          ) {
            return true;
          }

          return false;
        })
        .map(item => {
          return {
            ...item,
            pair: {
              ...item.pair,
              isMain: true
            }
          };
        });

      const result: Position[] = [];

      config?.CONTENT.poolsOrder.forEach((poolId: string) => {
        const pool = pools?.find(
          p => p.pair.id.toLowerCase() === poolId.toLowerCase()
        );

        if (pool) {
          result.push(pool);
        }
      });

      const lowered = config?.CONTENT.poolsOrder.map((p: string) =>
        p.toLowerCase()
      );
      const unordered = pools?.filter(
        p => !lowered?.includes(p.pair.id.toLowerCase())
      );

      return [...result, ...unordered];
    },
    {
      staleTime: 60000,
      refetchOnWindowFocus: true,
      enabled: !!config,
      ...options
    }
  );
};
